import { useContext, useState } from 'react'
import { useQuery } from '@apollo/client'
import CsvDownloader from 'react-csv-downloader'
import {
  GET_ALL_APPLICATION_IDS,
  GET_SESSION_BY_BG_ACCOUNT_ID,
  GET_USERS
} from '../../graphql/query'
import { useNavigate } from 'react-router-dom'
import UserContext from '../../context/UserContext'
import { MultiSelect } from 'react-multi-select-component'
import { IChatbotState } from '../../utils/interfaces/IChatbotState'
import ChatContext from '../../context/ChatContext'

interface IProps {
  setChatRoomId: any
}

const ChatHistoryReport: React.FC<IProps> = ({ setChatRoomId }) => {
  const { setState } = useContext(ChatContext)
  const { currentUserRole } = useContext(UserContext)
  const [selectedBots, setSelectedBots] = useState([])
  const [selectedAgents, setSelectedAgents] = useState([])
  const [applicationIds, setApplicationIds] = useState([])
  const [agentIds, setAgentIds] = useState([])

  const { data, loading, error } = useQuery(GET_SESSION_BY_BG_ACCOUNT_ID, {
    variables: {
      input: {
        applicationIds, // Directly use the state here
        agentIds
      }
    },
    context: {
      headers: {
        'access-control-allow-methods': 'Report'
      }
    }
  })

  const {
    loading: loading2,
    error: error2,
    data: data2
  } = useQuery(GET_ALL_APPLICATION_IDS, {
    variables: {
      input: {
        appIDString: null
      }
    },
    context: {
      headers: {
        'access-control-allow-methods': 'Report'
      }
    }
  })

  const {
    loading: loading4,
    error: error4,
    data: data4
  } = useQuery(GET_USERS, {
    skip: currentUserRole.agent || currentUserRole.operator,
    context: {
      headers: {
        'access-control-allow-methods': 'Report'
      }
    }
  })

  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 15
  const navigate = useNavigate()

  if (loading || loading2 || loading4) return <p>Loading...</p>

  if (error) return <p>Error: {error.message}</p>
  if (error2) return <p>Error: {error2.message}</p>
  if (error4) return <p>Error: {error4.message}</p>
  console.log(data)

  const sessions = data?.getSessionByBgaccountId || []
  const totalPages = Math.ceil(sessions.length / itemsPerPage)

  const indexOfLastItem = currentPage * itemsPerPage
  const indexOfFirstItem = indexOfLastItem - itemsPerPage
  const currentSessions = sessions.slice(indexOfFirstItem, indexOfLastItem)

  const handlePrevPage = () => {
    setCurrentPage((prevPage) => Math.max(prevPage - 1, 1))
  }

  const handleNextPage = () => {
    setCurrentPage((prevPage) => Math.min(prevPage + 1, totalPages))
  }

  const formatDate = (dateString: string) => {
    if (!dateString) return ''

    return new Date(dateString)
      .toLocaleString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false // 24-hour format
      })
      .replace(',', '') // Ensures CSV treats it as a single value
  }

  const calculateTimeDifference = (start: string, end: string) => {
    if (!start || !end) return ''

    const startTime = new Date(start)
    const endTime = new Date(end)
    const diffMs = endTime.getTime() - startTime.getTime()

    if (diffMs < 0) return '' // In case endDate is earlier than startDate

    const hours = Math.floor(diffMs / (1000 * 60 * 60))
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60))

    return `${hours}h ${minutes}m`
  }

  const tableData = sessions.map((session: any, index: number) => ({
    'Serial Number': (index + 1).toString(),
    'Bot Name': session?.botName || '',
    'Chat Room ID': `CONV-${session?.chatRoomId}`,
    'Assigned Agent': session?.agentName || '',
    'Resolved By': session?.reslovedBy ? 'By Agent' : 'By Bot',
    'Start Date': formatDate(session?.startDate),
    'End Date': formatDate(session?.endDate),
    'Chat Duration': calculateTimeDifference(session?.startDate, session?.endDate)
  }))

  const handleClick = (chatRoomId: any) => {
    setChatRoomId(chatRoomId)
    navigate(`/report/chatHistory-report/${chatRoomId}`)
    setState((prevState: IChatbotState) => {
      const existingPanel = prevState.panelBasedConversation.find(
        (panel) => panel.chatRoomId === chatRoomId
      )

      if (!existingPanel) {
        return {
          ...prevState,
          panelBasedConversation: [
            ...prevState.panelBasedConversation,
            {
              chatRoomId: parseInt(chatRoomId),
              conversation: []
            }
          ]
        }
      }
      return prevState
    })
  }

  const botOptions =
    data2?.allApplicationId?.map((bot: any) => ({
      label: bot?.appName,
      value: bot?.id
    })) || []

  const agentOptions =
    data4?.users?.map((user: any) => ({
      label: user.name,
      value: user.id
    })) || []

  const handleBotsChange = (selected: any) => {
    setSelectedBots(selected)
    setApplicationIds(selected.map((bot: any) => parseInt(bot.value, 10))) // Update applicationIds dynamically
  }

  const handleAgentsChange = (selected: any) => {
    setSelectedAgents(selected)
    setAgentIds(selected.map((agent: any) => parseInt(agent.value, 10))) // Update agentIds dynamically
  }

  return (
    <div>
      <div className='card mb-5 mb-xl-8'>
        <div className='d-flex justify-content-between'>
          <div className='card-header border-0 pt-5'>
            <h3 className='card-title align-items-start flex-column'>
              <span className='card-label fw-bold fs-3 mb-1'>Chat History Statistics</span>
              <span className='text-muted mt-1 fw-semibold fs-7'>
                {sessions?.length} chat sessions recorded
              </span>
            </h3>
          </div>
          <div className='p-8'>
            <CsvDownloader filename='chat_history' extension='.csv' datas={tableData}>
              <button className='btn btn-primary me-2'>
                <i className='bi bi-download fs-2 me-3'></i>
                Download Report
              </button>
            </CsvDownloader>
          </div>
        </div>
        <div className='d-flex px-9'>
          <div className='me-4'>
            <span className='fw-bolder text-dark align-items-center fs-4 fs-md-6'>
              Filter By Bot:
            </span>
            <div className='mb-4 mt-2'>
              <MultiSelect
                options={botOptions}
                value={selectedBots}
                onChange={handleBotsChange} // Handle changes here
                labelledBy='Select Bots'
                className='form-control w-300px'
              />
            </div>
          </div>
          <div className=''>
            <span className='fw-bolder text-dark align-items-center fs-4 fs-md-6'>
              Filter By Agent:
            </span>
            <div className='mb-4 mt-2'>
              <MultiSelect
                options={agentOptions}
                value={selectedAgents}
                onChange={handleAgentsChange} // Handle changes here
                labelledBy='Select Agents'
                className='form-control w-300px'
              />
            </div>
          </div>
        </div>
        <div className='card-body py-3 '>
          <div className='table-responsive border border-muted rounded-2'>
            <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
              <thead>
                <tr className='fw-bolder fs-6 text-primary bg-light-primary'>
                  <th className='ps-4 border-end min-w-75px'>S.no</th>
                  <th className='ps-4 border-end min-w-175px'>Bot Name</th>
                  <th className='ps-4 border-end min-w-175px'>Chat Room ID</th>
                  <th className='ps-4 border-end min-w-175px'>Assigned Agent</th>
                  {/* <th className='ps-4 border-end min-w-175px'>Timed Out</th>  */}
                  <th className='ps-4 border-end min-w-175px'>Resolved By</th>
                  <th className='ps-4 border-end min-w-175px'>Start Date</th>
                  <th className='ps-4 border-end min-w-175px'>End Date</th>
                  <th className='ps-4 border-end min-w-175px'>Chat Duration</th>
                </tr>
              </thead>
              <tbody>
                {currentSessions.map((session: any, index: number) => (
                  <tr
                    className='border-end'
                    key={index}
                    onClick={() => handleClick(session?.chatRoomId)}
                    style={{ cursor: 'pointer' }}
                    onMouseOver={(e) => (
                      (e.currentTarget.style.backgroundColor = '#f6f8fa'),
                      (e.currentTarget.style.textDecoration = 'underline'),
                      (e.currentTarget.style.textDecorationColor = '#7e7f85'),
                      (e.currentTarget.style.textDecorationThickness = '2px')
                    )}
                    onMouseOut={(e) => (
                      (e.currentTarget.style.backgroundColor = 'transparent'),
                      (e.currentTarget.style.textDecoration = 'none')
                    )}
                  >
                    <td className='ps-4 fw-bold'>{indexOfFirstItem + index + 1}</td>
                    <td className='ps-4 fw-bold'>{session?.botName}</td>
                    <td className='ps-4 fw-bold'>{session?.chatRoomId}</td>{' '}
                    <td className={`ps-4 fw-bold ${!session?.agentName ? 'text-muted' : ''}`}>
                      {session?.agentName || ''}
                    </td>
                    {/* <td className='ps-4 fw-bold'>{session?.timedOut ? 'TRUE' : 'FALSE'}</td>  */}
                    <td className='ps-4 fw-bold'>{session?.reslovedBy ? 'By Agent' : 'By Bot'}</td>
                    <td className={`ps-4 fw-bold ${!session?.startDate ? 'text-muted' : ''}`}>
                      {session?.startDate ? formatDate(session?.startDate) : ''}
                    </td>
                    <td className={`ps-4 fw-bold ${!session?.endDate ? 'text-muted' : ''}`}>
                      {session?.endDate ? formatDate(session?.endDate) : ''}
                    </td>
                    <td className='ps-4 fw-bold'>
                      {calculateTimeDifference(session?.startDate, session?.endDate)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className='d-flex justify-content-between mt-4'>
            <button
              className='btn btn-secondary'
              onClick={handlePrevPage}
              disabled={currentPage === 1}
            >
              Previous
            </button>
            <span className='align-self-center'>
              Page {currentPage} of {totalPages}
            </span>
            <button
              className='btn btn-secondary'
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ChatHistoryReport
